<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { Config } from '@/Config'
import * as Server from '@/services/Server'
import { useMainStore } from '@/stores/main'
import { formatDateTime } from '@/lib/dates'
import PageHeader from '@/components/PageHeader.vue'
import PlayerCrest from '@/components/PlayerCrest.vue'
import PlayerRanking from '@/components/PlayerRanking.vue'
import PlayerEventResults from '@/components/PlayerEventResults.vue'
import PlayerFastestLaps from '@/components/PlayerFastestLaps.vue'
import PlayerTitle from '@/components/PlayerTitle.vue'
import TrophyCabinet from '@/components/TrophyCabinet.vue'

const route = useRoute()
const store = useMainStore()

const username = computed(() => route.params.username)
const player = ref(null)
const loading = ref(true)
const notFound = ref(false)
const error = ref(false)

/**
 * The personal bests of the tracks the site knows (config/tracks.php, delivered by the bootstrap):
 * a time set on a track that is no longer configured has no page and no name to show.
 */
const fastestLaps = computed(() => {
  const known = new Set(store.tracks.map(track => track.id))

  return (player.value?.fastestLaps ?? []).filter(lap => known.has(lap.trackKey))
})

/** The personal-bests block exists only when the player has at least one time (see the template). */
const hasLaps = computed(() => fastestLaps.value.length > 0)

/**
 * One call for the whole page: the name shown as the title, the standing of the player, the awards
 * of the cabinet, the results in the running events and the personal bests. Every block is built
 * server-side (StandingsLib, EventAwardLib, EventLib, FastestLapLib) so the same data is not fetched
 * again for the other pages that show it.
 */
const fetchPlayer = () => {
  loading.value = true
  error.value = false
  notFound.value = false

  Server.get(Config.server.routes.api.player_detail(username.value))
    .then(response => {
      player.value = response.data?.data ?? null
    })
    .catch(err => {
      player.value = null
      notFound.value = err?.response?.status === 404
      error.value = !notFound.value
    })
    .finally(() => {
      loading.value = false
    })
}

onMounted(fetchPlayer)
watch(username, fetchPlayer)
</script>

<template>
  <main>
    <v-container>
      <div v-if="loading" class="d-flex justify-center py-12">
        <v-progress-circular indeterminate color="primary" />
      </div>

      <v-alert v-else-if="notFound" type="warning" variant="tonal">
        {{ $t('players.notFound') }}
      </v-alert>

      <v-alert v-else-if="error" type="error" variant="tonal">
        {{ $t('players.loadError') }}
      </v-alert>

      <template v-else-if="player">
        <PageHeader :title="player.name">
          <!-- The crest leads the name, as it does everywhere else on the site (see PlayerCrest).
               The title prop still feeds the breadcrumb. -->
          <template #title><PlayerCrest :crest="player.crest" />{{ player.name }}</template>
          <template #below-title>
            <div v-if="player.titles?.length" class="d-flex flex-wrap ga-1 mt-2">
              <PlayerTitle
                v-for="title in player.titles"
                :key="title.key"
                :title="title"
              />
            </div>
          </template>
        </PageHeader>

        <TrophyCabinet v-if="player.awards?.length" :awards="player.awards" class="mb-6" />

        <PlayerRanking :ranking="player.ranking" :period="player.period" class="mb-6" />

        <!-- Side by side while there is room for two columns (lg and up), stacked below. -->
        <v-row>
          <v-col cols="12" :lg="hasLaps ? 6 : 12">
            <PlayerEventResults :events="player.events" />
          </v-col>
          <v-col v-if="hasLaps" cols="12" lg="6">
            <PlayerFastestLaps :laps="fastestLaps" />
          </v-col>
        </v-row>

        <!-- The page is cached as a whole, so this is the only way to tell that something just
             happened and it is not on the page yet. -->
        <p v-if="player.updatedAt" class="text-caption text-medium-emphasis text-end mt-6 mb-0 pt-5">
          {{ $t('players.lastUpdated', { date: formatDateTime(player.updatedAt) }) }}
        </p>
      </template>
    </v-container>
  </main>
</template>
